import cloneDeepWith from 'lodash/cloneDeepWith';
import { keys, sort } from 'ramda';
import { omit, omitBy } from 'remeda';
import { z } from 'zod';

import type { UUID } from '../types/type-helpers';

export type ObjectWithKeys<KeysUnion extends string> = Record<KeysUnion, string>;

/**
 * Uppercases the first character in a string, leaving the rest alone.
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function removeNewLines(str: string): string {
  return str.replace(/(\r\n|\n|\r)/gm, '');
}

export function toFixedNumber(num: number, decimalPlaces: number): number {
  return parseFloat(num.toFixed(decimalPlaces));
}

/**
 * Sorts an array of objects to align with an array of ids.
 *
 * @param models An array of objects with ids to sort.
 * @param sortedKeys An array of ids to sort by.
 */
export function sortByIdArray<Model extends { id: UUID } = { id: UUID }>(
  models: Model[],
  sortedKeys: UUID[]
): Model[] {
  const sortByIds = sort<Model>((a, b) => {
    const aIndex = sortedKeys.indexOf(a.id);
    const bIndex = sortedKeys.indexOf(b.id);
    return aIndex - bIndex;
  });
  return sortByIds(models);
}

/**
 * Returns a new shuffled array.
 */
export function shuffle<T = unknown>(arr: T[]): T[] {
  return arr
    .map(value => ({ sortKey: Math.random(), value }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(a => a.value);
}

type PartialRecord<K extends string, V = unknown> = Partial<Record<K, V>>;

/**
 * Takes a grouping function `grouper` and an array,
 * returns an object of the array's items grouped by the keys `grouper` provides.
 *
 * @example
 *   const { even, odd } = groupBy(num => isEven(num) ? 'even' : 'odd', nums);
 */
export function groupBy<Value, Key extends string = string>(
  grouper: (val: Value) => Key,
  arr: Value[]
): PartialRecord<Key, Value[]> {
  const groups: PartialRecord<Key, Value[]> = {};
  arr.forEach(element => {
    const key = grouper(element);
    if (key === '__proto__') return;

    const existingValues = groups[key];
    if (!existingValues) {
      groups[key] = [element];
      return;
    }

    // Mutate in place - O(1) instead of O(n) array spread.
    existingValues.push(element);
  });

  return groups;
}
/**
 *
 * Takes a grouping function `grouper` and an array,
 * returns a map of the array's items grouped by the keys `grouper` provides.
 *
 * @example
 *   const parityMap = groupByAsMap(num => isEven(num) ? 'even' : 'odd', nums);
 *   // parityMap.get('even') -> [2, 4, 6]
 *   // parityMap.get('odd') -> [1, 3, 5]
 */
export function groupByAsMap<Value, Key extends string = string>(
  grouper: (val: Value) => Key,
  arr: Value[]
): Map<Key, Value[]> {
  const map = new Map<Key, Value[]>();
  arr.forEach(element => {
    const key = grouper(element);
    const existingValues = map.get(key);
    if (!existingValues) {
      map.set(key, [element]);
      return;
    }

    // Mutate in place - O(1) instead of O(n) array spread
    existingValues.push(element);
  });

  return map;
}

/**
 * Useful for type-guarding against nulls in a filter.
 *
 * @example
 *   const userOrNulls = [user1, null, ...];
 *   const result: User[] = userOrNulls.filter(isNotNull);
 */
export function isNotNull<T>(val: T | null): val is T {
  return val !== null;
}

/**
 * Useful for type-guarding against undefined's in a filter.
 *
 * @example
 *   const userOrUndefineds = [user1, undefined, ...];
 *   const result: User[] = userOrUndefineds.filter(isNotUndefined);
 */
export function isNotUndefined<T = unknown>(val: T | undefined): val is T {
  return val !== undefined;
}

/**
 * Useful for type-guarding against nulls or undefined's in a filter.
 *
 * @example
 *   const userOrAbsent = [user1, null, undefined, ...];
 *   const result: User[] = userOrAbsent.filter(isPresent);
 */
export function isPresent<T = unknown>(val?: T | null | undefined): val is T {
  return isNotUndefined(val) && isNotNull(val);
}

export function getIsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isEvenNum(num: number): boolean {
  return num % 2 === 0;
}

export function isOddNum(num: number): boolean {
  return num % 2 === 1;
}

export function isUUIDV4(string: string): boolean {
  return new RegExp(/^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i).test(
    string
  );
}

/**
 * Postgres rejects the NUL code point (`U+0000`) in both `text` and `jsonb`
 * columns ("unsupported Unicode escape sequence"), so a single stray NUL in
 * LLM output or pasted document content blows up the entire row write. Deep
 * strip every NUL from all string values, returning a structurally identical
 * value of the same type. Backed by lodash `cloneDeepWith`, so arrays, Dates,
 * and nested objects are preserved (unlike a JSON round-trip) and only string
 * leaves are rewritten.
 */
export function stripNullBytesDeep<T>(value: T): T {
  return cloneDeepWith(value, entry =>
    typeof entry === 'string' ? entry.replaceAll('\u0000', '') : undefined
  );
}

/**
 * Sleep/wait for a certain duration.
 */
export async function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

/**
 * Returns the frequency of each item in an array.
 */
export function frequency<T extends string = string>(arr: T[]): Record<T, number> {
  const start = {} as Record<T, number>;

  return arr.reduce<Record<T, number>>((acc, item) => {
    if (item === '__proto__') return acc;
    const count = acc[item] ?? 0;
    acc[item] = count + 1;
    return acc;
  }, start);
}

/**
 * Get top n most frequent items in an array.
 *
 * @note If n is not provided, returns all items.
 */
export function mostFrequent<T extends string = string>(arr: T[], n = Infinity): T[] {
  const freq: Record<T, number> = frequency(arr);
  const pairs = Object.entries(freq) as [T, number][];
  const frequentFirst = pairs.sort(([, aCount], [, bCount]) => bCount - aCount);
  return frequentFirst.slice(0, n).map(([item]) => item as T);
}

/**
 * Chunks an array into smaller arrays of a given size.
 */
export function chunk<T>(arr: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunks.push(arr.slice(i, i + chunkSize));
  }
  return chunks;
}

/** Checks whether a given string is in email format. */
export function isValidEmail(str: string): boolean {
  return z.string().trim().email().safeParse(str).success;
}

/**
 * Attempts the provided function, and returns either the success or error of the result.
 */
export function tryCatch<T = unknown, ErrorMsg extends Error = Error>(
  fn: () => T
): { ok: true; result: T } | { error: ErrorMsg; ok: false } {
  try {
    const result = fn();
    return { ok: true, result };
  } catch (error) {
    return { error, ok: false };
  }
}

/**
 * Attempts the provided async function, and returns either the success or error of the result.
 */
export async function tryCatchAsync<T = unknown, ErrorMsg extends Error = Error>(
  fn: () => Promise<T>
): Promise<{ ok: true; result: T } | { error: ErrorMsg; ok: false }> {
  try {
    const result = await fn();
    return { ok: true, result };
  } catch (error) {
    return { error, ok: false };
  }
}

/**
 * Takes an array of objects (all with the same keys K),
 * and returns a new object with keys K and values V[].
 *
 * @example
 *   const { a, b } = zipValuesByKey([{a: 1, b: '2'}, {a: 3, b: '4'}]);
 *   // a -> [1, 3]
 *   // b -> ['2', '4']
 */
export function zipValuesByKey<Item extends { [Key in keyof Item]: Item[Key] }>(
  objects: Item[]
): { [Key in keyof Item]?: Item[Key][] } {
  return objects.reduce<{ [Key in keyof Item]?: Item[Key][] }>((acc, obj) => {
    for (const key of keys(obj)) {
      if (key === '__proto__') continue;
      if (!acc[key]) acc[key] = [];
      acc[key]?.push(obj[key]);
    }

    return acc;
  }, {});
}

/**
 * Given an array of tuples, return a tuple of arrays of the two tuple elements
 */
export function unzip<First, Second>(tuples: [First, Second][]): [First[], Second[]] {
  const firsts: First[] = [];
  const seconds: Second[] = [];
  tuples.forEach(([first, second]) => {
    firsts.push(first);
    seconds.push(second);
  });
  return [firsts, seconds];
}

/**
 * Type-safe function to check if a value is an instance of Error.
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

/**
 * Type-safe version of Object.entries that preserves exact key types. Returns an array of tuples
 * where the first element is the exact key type and the second element is the corresponding value
 * type.
 */
export function objectEntries<T extends object>(
  obj: T
): Array<{ [K in keyof T]: [K, T[K]] }[keyof T]> {
  return Object.entries(obj) as Array<{ [K in keyof T]: [K, T[K]] }[keyof T]>;
}

/**
 * Similar to mapObjIndexed, but allows returning one or zero values for each initial value. If no
 * value is returned for a key, that key is excluded from the returned object.
 */
export function flatMapObjectValues<V1, V2, K extends string>(
  obj: Record<K, V1>,
  fn: (value: V1, key: K) => [V2] | []
): Record<K, V2> {
  const mappedObj: Record<string, V2> = Object.fromEntries(
    objectEntries(obj).flatMap(([key, value]) => fn(value, key).map(newValue => [key, newValue]))
  );
  return mappedObj as Record<K, V2>;
}

/**
 * Alternative to { ...objectA, ...objectB } that automatically skips any properties in objectB
 * whose values are undefined. This is important to avoid overriding required properties with
 * undefined, which compromises type safety since TypeScript isn't aware of this risk.
 */
export function mergeWithDefaults<T extends object, U extends object>(
  values: T,
  defaults: U
): T & U {
  const valuesWithoutUndefined = omitBy(
    values,
    (value, key) => value === undefined && key in defaults
  ) as unknown as T;

  return { ...defaults, ...valuesWithoutUndefined };
}

/**
 * More type-safe version of Ramda's mapObjIndexed function that (unlike Ramda's version) correctly
 * handles optional properties in the function argument type.
 */
export function mapObjectValues<T extends object, U>(
  obj: T,
  fn: (value: T[keyof T], index: keyof T) => U
): { [K in keyof T]: U } {
  const result = {} as { [K in keyof T]: U };

  for (const key in obj) {
    if (key === '__proto__') continue;
    result[key] = fn(obj[key], key);
  }

  return result;
}

/**
 * AWS SDK has a bit of an awkward api and will throw if you try to publish more than 10 messages at a time,
 * so we have to manually create batches ourselves.
 *
 * Typical batch in AWS is 10.  See https://aws.amazon.com/about-aws/whats-new/2021/11/amazon-sns-supports-publishing-batches-messages-single-api-request/.
 */
export function batchArray<T>(array: T[], batchNum: number = 10): T[][] {
  const indices = { start: 0 };
  const batches: T[][] = [];

  while (indices.start <= array.length) {
    const batch = array.slice(indices.start, indices.start + batchNum);
    if (batch.length) batches.push(batch);
    indices.start += batchNum;
  }

  return batches;
}

export interface RetryWithBackoffOptions {
  /** Initial delay in ms (default: 1000) */
  initialDelay?: number;
  /** Optional logger for retry attempts */
  logger?: { info: (message: string) => void };
  /** Maximum delay in ms (default: 10000) */
  maxDelay?: number;
  /** Maximum number of retries (default: 5) */
  maxRetries?: number;
  /** Optional callback to determine if error should trigger retry (default: retry all errors) */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Retries an operation with exponential backoff and optional jitter.
 *
 * @param operation - The async operation to retry
 * @param options - Configuration options for retry behavior
 * @returns The result of the operation
 *
 * @example
 *   // Simple retry with defaults
 *   const result = await retryWithBackoff(() => fetchData());
 *
 * @example
 *   // Retry only on specific errors
 *   await retryWithBackoff(
 *     () => sdk.FinishFieldActions({ workflowId }),
 *     {
 *       maxRetries: 3,
 *       initialDelay: 100,
 *       shouldRetry: (error) =>
 *         error instanceof Error && error.message.includes('specific error'),
 *     }
 *   );
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: RetryWithBackoffOptions = {}
): Promise<T> {
  const {
    initialDelay = 1000,
    logger,
    maxDelay = 10000,
    maxRetries = 5,
    shouldRetry = () => true,
  } = options;

  const state = {
    delay: initialDelay,
    retries: 0,
  };

  while (true) {
    try {
      return await operation();
    } catch (error) {
      const canRetry = state.retries < maxRetries && shouldRetry(error);
      if (!canRetry) throw error;

      logger?.info(
        `retryWithBackoff: Operation failed, retrying in ${Math.round(state.delay)}ms... (${state.retries + 1}/${maxRetries})`
      );
      await sleep(state.delay);

      // Exponential backoff with jitter to prevent thundering herd
      state.delay = Math.min(state.delay * 1.5, maxDelay);
      state.delay = state.delay * (0.9 + Math.random() * 0.2);
      state.retries++;
    }
  }
}

type KeyWithValueType<T extends object, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

export function sumBy<T>(objs: T[], fn: (obj: T, index: number) => number): number {
  let sum = 0;
  for (const [index, obj] of arrayEntries(objs)) sum += fn(obj, index);
  return sum;
}

export function sumOf<T extends object>(objs: T[], key: KeyWithValueType<T, number>): number {
  return sumBy(objs, obj => obj[key] as number);
}

export function maxValueBy<T>(objs: T[], fn: (obj: T) => number, min = -Infinity): number {
  let result = min;

  for (const obj of objs) {
    const value = fn(obj);
    if (value > result) result = value;
  }

  return result;
}

export function maxValueOf<T extends object>(
  objs: T[],
  key: KeyWithValueType<T, number>,
  min = -Infinity
): number {
  return maxValueBy(objs, obj => obj[key] as number, min);
}

/**
 * Safer version of Array.prototype.entries.
 */
export function arrayEntries<T>(arr: T[]): [number, T][] {
  return arr.map((x, i) => [i, x]);
}

/**
 * Type utils for typedGroupBy, namespaced to avoid conflicts with other utils.
 */
namespace TypedGroupBy {
  export type Predicate<T, U extends T> = (value: T) => value is U;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export type AnyGroupPredicates<T> = Record<string, Predicate<T, any>>;

  /**
   * Get the type asserted by a predicate.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type GetAssertedType<P> = P extends Predicate<any, infer U> ? U : never;

  /**
   * Get all types asserted by a set of predicates.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type GetAllAssertedTypes<GroupPredicates extends AnyGroupPredicates<any>> = GetAssertedType<
    GroupPredicates[keyof GroupPredicates]
  >;

  /**
   * The type of the resulting groups object. Map each predicate in groupPredicates to its asserted
   * type as an array.
   */
  export type Groups<T, GroupPredicates extends AnyGroupPredicates<T>> = {
    [K in keyof GroupPredicates]: GetAssertedType<GroupPredicates[K]>[];
  };

  /**
   * The type of a value that is no assignable to any predicate; any such values are returned on
   * the resulting rest array.
   */
  export type Rest<T, GroupPredicates extends AnyGroupPredicates<T>> = Exclude<
    T,
    GetAllAssertedTypes<GroupPredicates>
  >;
}

/**
 * More advanced version of Lodash's groupBy function. Groups value according to type predicates
 * into strongly typed groups.
 *
 * @param values An array of values
 * @param groupPredicates An object mapping arbitrary keys to type predicates
 * @returns A groups object that maps each key to the array of values matching the predicate, and a
 *   rest array of values that didn't match any predicate
 */
export function typedGroupBy<T, GroupPredicates extends TypedGroupBy.AnyGroupPredicates<T>>(
  values: T[],
  groupPredicates: GroupPredicates
) {
  type RestValue = TypedGroupBy.Rest<T, GroupPredicates>;

  const groups = mapObjectValues(groupPredicates, () => [] as unknown[]) as TypedGroupBy.Groups<
    T,
    GroupPredicates
  >;

  const rest: RestValue[] = [];

  valueLoop: for (const value of values) {
    for (const key in groupPredicates) {
      const predicate = groupPredicates[key];
      if (predicate(value)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        groups[key].push(value as any);
        continue valueLoop;
      }
    }

    rest.push(value as RestValue);
  }

  return { groups, rest };
}

export function omitDeep<T extends object, K extends string>(obj: T, keys: K[]): unknown {
  function recurse(value: unknown) {
    if (value && typeof value === 'object') return omitDeep(value, keys);
    return value;
  }

  if (Array.isArray(obj)) return obj.map(recurse);

  const omittedObject = omit(obj, keys as (keyof object)[]);
  return mapObjectValues(omittedObject, recurse);
}
